import { z } from 'zod';
import { toast } from 'sonner';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMoveForm } from '@/hooks/form/use-form';
import { useGetAllFolders } from '@/hooks/folder';
import type { Folder } from '@/types';

const moveFormSchema = z.object({
  targetFolderId: z.string().min(1, 'Please select a target folder'),
});

type MoveFormData = z.infer<typeof moveFormSchema>;

interface MoveFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: string;
  formName: string;
  currentFolderId?: string | null;
  onSuccess?: () => void;
}

export const MoveFormModal: React.FC<MoveFormModalProps> = ({
  isOpen,
  onClose,
  formId,
  formName,
  currentFolderId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const moveFormMutation = useMoveForm();
  const { data: foldersData, isPending: isFoldersPending } = useGetAllFolders();

  const form = useForm<MoveFormData>({
    resolver: zodResolver(moveFormSchema),
    defaultValues: {
      targetFolderId: '',
    },
  });

  const onSubmit = async (data: MoveFormData) => {
    try {
      setIsSubmitting(true);
      
      // If the target folder is the same as current folder, don't do anything
      if (data.targetFolderId === currentFolderId) {
        toast.info(t('common.noChangesToSave'));
        onClose();
        return;
      }

      await moveFormMutation.mutateAsync({
        formId,
        targetFolderId: data.targetFolderId,
      });
      
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error moving form:', error);
      toast.error(t('common.operationFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  // Filter out the current folder from the list
  const availableFolders = foldersData?.filter(folder => folder.id !== currentFolderId) || [];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon icon="material-symbols:drive-file-move" className="text-xl" />
            {t('formManagement.moveForm')}
          </DialogTitle>
          <DialogDescription>
            {t('formManagement.moveFormDescription', { formName })}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="targetFolderId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('formManagement.selectTargetFolder')}</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      disabled={isSubmitting || isFoldersPending}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('formManagement.selectFolder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableFolders.map((folder: Folder) => (
                          <SelectItem key={folder.id} value={folder.id}>
                            <div className="flex items-center gap-2">
                              <Icon icon="material-symbols:folder" className="text-sm" />
                              {folder.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || moveFormMutation.isPending}
                className="main-dark-button"
              >
                {isSubmitting || moveFormMutation.isPending ? (
                  <>
                    <Icon icon="eos-icons:loading" className="mr-2" />
                    {t('formManagement.moving')}
                  </>
                ) : (
                  <>
                    <Icon icon="material-symbols:drive-file-move" className="mr-2" />
                    {t('formManagement.move')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
